import React, { useState, useMemo } from 'react';
import { Box, Grid, Stack, Button, Alert } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import {
  DonutChart,
  HorizontalBarChart,
  ProgressBarChart,
  DemographicsChart,
  AgeDistributionChart,
} from '../../../../../shared/components/charts';
import { DashboardCard, DashboardCardSkeleton, CohortGuard } from '../../../../../shared/components';
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  ComparisonToggle,
  ComparisonColumns,
} from '../../../../../shared/components';
import { ProgrammeStatsColumn } from '../components/ProgrammeStatsColumn';
import {
  useProgrammeProfileStats,
  useAgeDistribution,
  useWorkingEnvironment,
  useDemographics,
  useTravelTime,
  useProgrammeGoals,
  useWorkingHourPatterns,
  useActiveUsersByDepartment,
} from '../hooks/useProgrammeProfile';
import { useProgrammeBreakdowns } from '../hooks/useProgrammeBreakdowns';
import { buildFilterParams } from '../../shared/utils';
import { transformProgrammeGoals } from '../utils/transformData';
import { useClientScope } from '../../../../../shared/contexts';
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsStats,
  useComparisonMode,
  useComparisonGroups,
} from '../../../../../shared/hooks';
import { AiSummaryPanel, useAiSummary } from '../../../insights';
import type { ComparisonSummaryRequest } from '../../../insights';

export const ProgrammeProfile: React.FC = () => {
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId, null, null, { isCentralAdmin }),
    [filters, dateRange, effectiveClientId, isCentralAdmin],
  );

  // Comparison mode (applies to the headline engagement stat cards only).
  const comparison = useComparisonMode(filters);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filters,
    dateRange,
    effectiveClientId,
    comparison,
    isCentralAdmin,
  );

  const { buildPayload: buildBasePayload, disabled: aiDisabled } = useAiSummary({
    dashboard: 'Programme Profile',
    groups,
    isComparing,
    dimension: comparison.dimension,
    filterParams,
    metricSpecs: [
      { label: 'Total users', keys: ['total_users'] },
      { label: 'Active users', keys: ['active_users'] },
      { label: 'Participation rate', keys: ['participation_rate'] },
      { label: 'Completion rate', keys: ['completion_rate'] },
    ],
  });

  const { fetchBreakdowns } = useProgrammeBreakdowns(filterParams, effectiveClientId);

  const buildAiPayload = async (): Promise<ComparisonSummaryRequest> => {
    const payload = buildBasePayload();

    if (!isComparing && effectiveClientId) {
      const breakdowns = await fetchBreakdowns();
      if (Object.keys(breakdowns).length > 0 && payload.groups?.[0]) {
        // Transform breakdowns to match expected type (null -> undefined for rag_label)
        const transformedBreakdowns = Object.fromEntries(
          Object.entries(breakdowns).map(([key, entries]) => [
            key,
            entries.map(({ label, value, rag_label }) => ({
              label,
              value,
              rag_label: rag_label ?? undefined,
            })),
          ]),
        );
        payload.groups[0].breakdowns = transformedBreakdowns;
      }
    }

    return payload;
  };

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const { error: statsError } = useProgrammeProfileStats(filterParams);

  const {
    data: ageDistributionData,
    isLoading: ageDistributionLoading,
    error: ageDistributionError,
  } = useAgeDistribution(filterParams);

  const {
    data: workingEnvironmentData,
    isLoading: workingEnvironmentLoading,
    error: workingEnvironmentError,
  } = useWorkingEnvironment(filterParams);

  const {
    data: demographicsData,
    isLoading: demographicsLoading,
    error: demographicsError,
  } = useDemographics(filterParams);

  const { data: travelTimeData, isLoading: travelTimeLoading, error: travelTimeError } = useTravelTime(filterParams);

  const {
    data: programmeGoalsData,
    isLoading: programmeGoalsLoading,
    error: programmeGoalsError,
  } = useProgrammeGoals(filterParams);

  const {
    data: workingHourPatternsData,
    isLoading: workingHourPatternsLoading,
    error: workingHourPatternsError,
  } = useWorkingHourPatterns(filterParams);

  const {
    data: activeUsersByDepartmentData,
    isLoading: activeUsersByDepartmentLoading,
    error: activeUsersByDepartmentError,
  } = useActiveUsersByDepartment(filterParams);

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const transformedProgrammeGoals = useMemo(() => transformProgrammeGoals(programmeGoalsData), [programmeGoalsData]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <ComparisonToggle comparison={comparison} />
            <DurationSelector value={dateRange} onChange={handleDateRangeChange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
          dateRange={dateRange}
        />

        {statsError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            Failed to load statistics. Please try again.
          </Alert>
        )}

        {/* Headline engagement metrics, one column per comparison group. */}
        <ComparisonColumns groups={groups} overflowCount={overflowCount}>
          {(group) => <ProgrammeStatsColumn filterParams={group.filterParams} stacked={isComparing} />}
        </ComparisonColumns>

        <DashboardCard title='AI Summary' tooltip='AI narrative of the metrics shown.' expandable={false}>
          <AiSummaryPanel
            title={isComparing ? 'Compare these groups with AI' : 'Summarise this view with AI'}
            buildPayload={buildAiPayload}
            disabled={aiDisabled}
          />
        </DashboardCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            {ageDistributionLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : ageDistributionError ? (
              <DashboardCard title='Age Distribution' subtitle='Organisation Demographic'>
                <Alert severity='error'>Failed to load age distribution data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title='Age Distribution'
                subtitle='Organisation demographics'
                details='The age distribution across your organisation. Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.'
              >
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <AgeDistributionChart
                    data={
                      ageDistributionData?.data?.distribution.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) || []
                    }
                    height={220}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {workingEnvironmentLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : workingEnvironmentError ? (
              <DashboardCard
                title='Working environment'
                subtitle='Percentage of employees based in various environment'
              >
                <Alert severity='error'>Failed to load working environment data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title='Working environment'
                subtitle='Percentage of employees based in various environments'
                details='The percentage of employees based in each working environment. Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.'
              >
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <DonutChart
                    data={
                      workingEnvironmentData?.data?.distribution.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) || []
                    }
                    height={220}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {demographicsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : demographicsError ? (
              <DashboardCard title='Employee Demographics Overview' subtitle='Breakdown of participants by gender'>
                <Alert severity='error'>Failed to load demographics data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title='Employee Demographics Overview'
                subtitle='Breakdown of participants by gender'
                details='A breakdown of participants by gender. Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.'
              >
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <DemographicsChart data={demographicsData?.data?.distribution || []} />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {travelTimeLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : travelTimeError ? (
              <DashboardCard title='Travel Time' subtitle='Frequency of travel commitments across the workforce'>
                <Alert severity='error'>Failed to load travel time data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title='Travel Time'
                subtitle='Frequency of travel commitments across the workforce'
                details='The frequency of travel commitments across the workforce. Use this view to spot changes over time, compare segments, and identify outliers worth a closer look.'
              >
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <DonutChart
                    data={
                      travelTimeData?.data?.distribution.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) || []
                    }
                    height={220}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {programmeGoalsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : programmeGoalsError ? (
              <DashboardCard title='Programme Goals' subtitle='Understanding what employees want to achieve'>
                <Alert severity='error'>Failed to load programme goals data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard title='Programme Goals' subtitle='Understanding what employees want to achieve'>
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <ProgressBarChart data={transformedProgrammeGoals} />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {workingHourPatternsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : workingHourPatternsError ? (
              <DashboardCard title='Working Hour Patterns' subtitle='Typical work schedule distribution'>
                <Alert severity='error'>Failed to load working hour patterns data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard title='Working Hour Patterns' subtitle='Typical work schedule distribution'>
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <DonutChart
                    data={
                      workingHourPatternsData?.data?.distribution.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) || []
                    }
                    height={220}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 12 }}>
            {activeUsersByDepartmentLoading ? (
              <DashboardCardSkeleton title subtitle height={380} />
            ) : activeUsersByDepartmentError ? (
              <DashboardCard title='Active Users by Department' subtitle='Participation levels segmented by department'>
                <Alert severity='error'>Failed to load active users by department data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard title='Active Users by Department' subtitle='Participation levels segmented by department'>
                <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortQuery.isLoading}>
                  <HorizontalBarChart
                    data={
                      activeUsersByDepartmentData?.data?.distribution.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) || []
                    }
                    height={380}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
